import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AxiosError } from "axios";
import moment from "moment";

import {
  getEnvironment,
  isAutoEnrolledInventoryProgram,
  isFeatureActiveOnCurrentFarm,
  isFieldEnrolled,
  isImpersonating,
  isSoilPlus3DataGapActiveForProgram,
  useAddFieldCropPractice,
  useAddFieldPracticeParticipations,
  useDeleteFieldCropPractice,
  useDeleteFieldPracticeParticipations,
  useGetAllCarbonProgramOutcomes,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldPractices,
  useGetFieldShape,
  useGetPracticeCrops,
  useGetVerificationEditingRequests,
  useLocation,
  useNavigate,
  useTranslation,
  useUpdateFieldCropPractice,
  useUpdateVerificationEditingRequest,
  useValidateCropPeriods,
} from "utils";

import { FEATURE_FLAG_SLUGS } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  CROP_TYPE_FCPO,
  CarbonProgram,
  CropFCPO,
  CropFormValidationError,
  EDITING_REQUEST_TYPE,
  FertilizerManagement,
  FieldListField,
  PRACTICE_SLUG,
  PRACTICE_TYPE,
  PracticeBundle,
  PracticeCrop,
  PracticeFCPO,
  PracticeSlug,
  RequirementLevel,
  VIEW_TYPE,
  ZERO_UUID,
} from "models";

import {
  AlertBanner,
  Button,
  Divider,
  Drawer,
  FieldHeadline,
  Flex,
  Form,
  HideFCPOFeatureForUnenrolledFarms,
  HideFeature,
  Modal,
  Skeleton,
  StickyFooterButtons,
  Typography,
  Message as message,
} from "components";

import { PracticeBundleControls } from "pages/app/all-field-view/components/PracticeBundleControls.tsx";
import { PracticeEnrolmentNudgeCard } from "pages/app/all-field-view/components/PracticeEnrolmentNudgeCard.tsx";
import { UndersowingOverlapWarningModal } from "pages/app/all-field-view/components/UndersowingOverlapWarningModal.tsx";
import { ReviewDraftTag } from "pages/app/all-field-view/components/forms/ReviewDraftTag";
import { AddEditCropForm } from "pages/app/all-field-view/components/forms/crop/AddEditCropForm";
import { AddEditOrganicAndMineralFertilizers } from "pages/app/all-field-view/components/forms/fertilizers/AddEditOrganicAndMineralFertilizers";
import { FertilizerManagementYesNoForm } from "pages/app/all-field-view/components/forms/fertilizers/FertilizerManagementYesNoForm";
import { AddEditTillageForm } from "pages/app/all-field-view/components/forms/tillage/AddEditTillageForm";
import { useCropDataToReview } from "pages/app/all-field-view/hooks/useCropDataToReview";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import {
  createCropItems,
  createCropPracticeRequestBody,
  getCropYearFromDates,
  getFieldBaselinePeriod,
  isSownInBaselinePeriod,
} from "pages/app/all-field-view/utils/AllFieldsUtils";
import { getUndersowingCropsInvalidatedByChange } from "pages/app/all-field-view/utils/CropValidationUtils";
import {
  getCropRecommendedDates,
  getFirstEditableYear,
  getIsBaselineOrHistoricYear,
  getIsSoilPlus3DataCollectionEditable,
  getPracticeInputsDisabled,
} from "pages/app/all-field-view/utils/dataEntryUtils.ts";
import {
  getDiverseCoverCropIncentive,
  getInputRequirementConfig,
  isRequired,
  isRequiredForSaving,
  shouldShowInput,
} from "pages/app/all-field-view/utils/getInputRequirementConfig";
import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { EditingRequestBanner } from "pages/app/field-timeline/components/EditingRequestBanner";
import { getBaselineYearsByPracticeType } from "pages/app/field-timeline/utils/PracticeUtils.ts";

export type FormPracticeObject = Record<string, Record<string, string | moment.Moment[]>>;

const MAX_VALIDATION_ATTEMPTS = 10;
const VALIDATION_DELAY_MS = 150;

export const AddEditCropDrawer = ({
  availablePracticeBundles,
  field,
  existingFertilizers, // to be used in forms
  existingTillages, // to be used in forms
  existingCrop, // to be used in forms
  allCrops,
  showDrawer,
  setShowDrawer,
  year,
  viewType,
  fertilizerManagements = [],
  formWithOnLoadValidation = false,
  carbonProgram,
  farmId,
}: {
  availablePracticeBundles: PracticeBundle[];
  field: FieldListField;
  existingFertilizers: PracticeFCPO[];
  existingTillages: PracticeFCPO[];
  existingCrop: CropFCPO; // one crop object
  allCrops: CropFCPO[];
  showDrawer: boolean;
  setShowDrawer: (show: boolean) => void;
  year: number;
  viewType?: VIEW_TYPE;
  fertilizerManagements: FertilizerManagement[];
  formWithOnLoadValidation?: boolean;
  carbonProgram: CarbonProgram | null | undefined;
  farmId: string | undefined;
}) => {
  const isSoilPlus3DataGapFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);
  const { t } = useTranslation(["allFieldView", "tasks"]);
  const [form] = Form.useForm();
  const { validateCropChanges } = useValidateCropPeriods();
  const { cmcMixCropFCPO, selectedRecommendationItemOnTimeline, showDrawerFromCMC, setShowDrawerFromCMC, isNewCropMix } = useCropMixComposerContext();
  const isSideCropRecommendationPlaceholder = selectedRecommendationItemOnTimeline?.practiceType === PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION;
  const { currentFarm } = useGetFarms();
  const { data: practicesCrops } = useGetPracticeCrops({ regionId: field?.regionId });

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = `${location.pathname.split("cmc")[0].replace(/\/$/, "")}/`;

  const { fieldShape, fieldShapeLoading } = useGetFieldShape({ fieldId: field.id });
  const { mutateAsync: addFieldCropPractice } = useAddFieldCropPractice({ fieldId: field.id, farmId });
  const { mutateAsync: updateFieldCropPractice } = useUpdateFieldCropPractice({ fieldId: field.id, farmId });
  const { mutateAsync: deleteFieldCropPractice } = useDeleteFieldCropPractice({
    fieldId: field.id,
    farmId,
  });
  const { mutate: addFieldPracticeParticipations } = useAddFieldPracticeParticipations(field?.id ? [field?.id] : [], currentFarm?.id);
  const { mutate: deleteFieldPracticeParticipations } = useDeleteFieldPracticeParticipations(field?.id ? [field?.id] : [], currentFarm?.id);

  const { data: fieldPractices } = useGetFieldPractices({ fieldId: field.id ?? "", inView: true });

  const { data: editingRequests } = useGetVerificationEditingRequests({ farmId: farmId ?? "" });
  const { mutateAsync: submitEditingRequestComment } = useUpdateVerificationEditingRequest({ farmId: farmId ?? "" });

  // we can refactor to a helper function && move to a util if needed in another place, but let's keep it here for now
  const pendingCropVerificationRequest = editingRequests?.find(
    (r) =>
      r.requestType === EDITING_REQUEST_TYPE.CROP &&
      r.completedAt === null &&
      (r.fieldCropId === existingCrop.id ||
        (!existingCrop.id && r.fieldCropId === null && r.year === year && r.fieldId === field.id && r.cropType === existingCrop.cropType))
  );

  // a verifier asked for this missing crop, so it can still be added to its year even when that year is otherwise locked
  const missingCropRequestYear = !existingCrop.id ? pendingCropVerificationRequest?.year ?? undefined : undefined;

  const pendingTillageVerificationRequests =
    editingRequests?.filter(
      (r) => r.requestType === EDITING_REQUEST_TYPE.TILLAGE && r.completedAt === null && existingTillages.some((tillage) => tillage.id === r.fieldTillageId)
    ) ?? [];

  const pendingFertilizerVerificationRequests =
    editingRequests?.filter(
      (r) =>
        (r.requestType === EDITING_REQUEST_TYPE.MINERAL_FERTILIZER || r.requestType === EDITING_REQUEST_TYPE.ORGANIC_FERTILIZER) &&
        r.completedAt === null &&
        existingFertilizers.some((fertilizer) => fertilizer.id === r.fieldMineralFertilizerId || fertilizer.id === r.fieldOrganicFertilizerId)
    ) ?? [];

  const pendingSeedingVerificationRequest = editingRequests?.find(
    (r) =>
      r.requestType === EDITING_REQUEST_TYPE.SEEDING &&
      r.completedAt === null &&
      !!existingCrop.seedingPracticeId &&
      r.fieldSeedingId === existingCrop.seedingPracticeId
  );

  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(farmId || null);

  const { isSubmissionFeatureEnabled, isSubmissionPeriod, isDataSubmitted, submissionStatusValue } = useSubmissionStatus(activeFarmReportingYear);
  const fieldIsEnrolled = isFieldEnrolled(availablePracticeBundles, carbonProgram ?? null);
  const { dataToReview, isPracticeToReview } = useCropDataToReview({
    crop: existingCrop,
    fieldId: field.id,
    isFieldEnrolled: fieldIsEnrolled || isAutoEnrolledInventoryProgram(carbonProgram),
    isSubmissionPeriodOpen: isSubmissionFeatureEnabled && isSubmissionPeriod,
  });
  const renderReviewTag = (practice: PracticeFCPO) => (isPracticeToReview(practice) ? <ReviewDraftTag /> : null);
  const firstEditableYear = getFirstEditableYear(activeFarmReportingYear, submissionStatusValue?.lastSubmissionYear);
  const mineralFertilizers = existingFertilizers.filter((fertilizer) => fertilizer.type === PRACTICE_SLUG.MINERAL_FERTILIZERS);
  const organicFertilizers = existingFertilizers.filter((fertilizer) => fertilizer.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS);

  const farmIsEnrolledInCarbonProgram = !!carbonProgram?.id;
  const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  // We need the carbon runner outcomes to figure out which inputs should be required
  const { data: carbonProgramOutcomes, isLoading: isLoadingProgramOutcomes } = useGetAllCarbonProgramOutcomes(farmId, carbonProgram);

  const carbonProgramResultOfField = carbonProgramOutcomes?.[field.id];

  const currentYear = moment().year();
  const isCurrentYear = year === currentYear;

  const [showExceptZeroValueWarning, setShowExceptZeroValueWarning] = useState(false);
  const [undersowingCropsBrokenByChange, setUndersowingCropsBrokenByChange] = useState<string[]>([]);
  const [fieldsTouched, setFieldsTouched] = useState<boolean>(false);
  const [cropPeriodValidationErrors, setCropPeriodValidationErrors] = useState<CropFormValidationError[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCropDeletionModal, setShowCropDeletionModal] = useState<boolean>(false);
  const [optionalInputsAreShown, setOptionalInputsAreShown] = useState<boolean>(true);
  const [practiceBundles, setPracticeBundles] = useState(() =>
    availablePracticeBundles.length > 0 ? availablePracticeBundles : fieldPractices?.availablePracticeBundles ?? []
  );
  const [showModal, setShowModal] = useState<boolean>(false);
  const [initialValidationDone, setInitialValidationDone] = useState(false);
  const [recommendedSeedingDate, setRecommendedSeedingDate] = useState<string>(
    existingCrop.seedingStartDate ?? existingCrop.fallbackDates.seedingStartDate ?? ""
  );
  const [recommendedHarvestDate, setRecommendedHarvestDate] = useState<string>(existingCrop.harvestEndDate ?? existingCrop.fallbackDates.harvestEndDate ?? "");

  const tillageRef = useRef<HTMLDivElement | null>(null);
  const fertilizerRef = useRef<HTMLDivElement | null>(null);
  const seedingRef = useRef<HTMLDivElement | null>(null);

  const baselineYearsByPracticeType = carbonProgramResultOfField ? getBaselineYearsByPracticeType(carbonProgramResultOfField) : {};

  const handleBundleActiveStateChange = async (bundleSlug: PracticeSlug, value: boolean) => {
    setFieldsTouched(true);

    const practiceSlugMap = {
      crops: ["crops"],
      // Only needed until we remove the feature flag for Soil+ 3.0
      tillages: [PRACTICE_SLUG.TILLAGES, PRACTICE_SLUG.SEEDINGS],
      // Only needed until we remove the feature flag for Soil+ 3.0
      organicFertilizers: [PRACTICE_SLUG.ORGANIC_FERTILIZERS, PRACTICE_SLUG.MINERAL_FERTILIZERS],
    };

    // @ts-expect-error: Will be removed anyway
    let practiceSlugs = bundleSlug === PRACTICE_SLUG.CROPS && !value ? Object.values(PRACTICE_SLUG) : practiceSlugMap[bundleSlug];
    if (isSoilPlus3DataGapFeatureActive) {
      practiceSlugs = [bundleSlug];
    }

    if (value) {
      addFieldPracticeParticipations([{ fieldId: field.id, practiceSlugs }]);
    } else {
      deleteFieldPracticeParticipations({ fieldId: field.id, practiceSlugs });
    }

    const updatedBundle = { ...practiceBundles.find((bundle) => bundle.slug === bundleSlug)!, active: value };
    const newArray = [...practiceBundles.filter((bundle) => bundle.slug !== bundleSlug), updatedBundle];

    setPracticeBundles(newArray);
  };

  // Toggle handler for past year inputs
  const handlePastYearToggle = useCallback(() => {
    setFieldsTouched(true);
  }, []);

  const handleCropDeletion = useCallback(async () => {
    if (!field?.id || !existingCrop?.id) return;

    try {
      await deleteFieldCropPractice({ cropId: existingCrop.id });
      message({ content: t("cropForm.cropDeletionModal.success"), icon: "tick" });
      setShowDrawer(false);

      if (existingCrop.cropMixId) {
        navigate(basePath);
      }

      if (showDrawerFromCMC) {
        setShowDrawerFromCMC(false);
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError && err.code === "ERR_CANCELED") {
        message({ content: t("cropForm.cropDeletionModal.fail"), icon: "error" });
      }
    }
  }, [
    basePath,
    existingCrop?.cropMixId,
    navigate,
    field?.id,
    existingCrop?.id,
    deleteFieldCropPractice,
    t,
    setShowDrawer,
    showDrawerFromCMC,
    setShowDrawerFromCMC,
  ]);

  // A request for a missing crop can also be answered with a comment alone, without adding the crop.
  const submitMissingCropRequestComment = async (comment: string) => {
    if (!pendingCropVerificationRequest) return;

    try {
      await submitEditingRequestComment({ requestId: pendingCropVerificationRequest.id, userComment: comment });
      message({ content: t("tasks:editingRequestBanner.successMessage"), icon: "tick" });
      setShowDrawer(false);

      if (showDrawerFromCMC) {
        setShowDrawerFromCMC(false);
      }
    } catch {
      // the global error banner already tells the farmer the comment wasn't saved, so the drawer stays open
    }
  };

  const onSubmit = async (e: React.MouseEvent<HTMLButtonElement>, skipWarning?: boolean) => {
    setIsLoading(true);

    await form.validateFields().catch(() => {});

    const cropFromFormField = form.getFieldValue("crop");
    const cropToValidate = {
      ...existingCrop,
      seedingStartDate: cropFromFormField?.seedingDateRange[0],
      harvestEndDate: cropFromFormField?.harvestDateRange[1],
    };
    const cropPeriodValidationErrors = validateCropChanges([{ fieldId: field.id, crop: cropToValidate, requestedYear: missingCropRequestYear }], {
      [field.id]: allCrops,
    });

    if (!cropFromFormField?.seedingDateRange[0] || !cropFromFormField?.harvestDateRange[1]) {
      cropPeriodValidationErrors.push({ fieldId: field.id, message: t("errors.required") });
      setCropPeriodValidationErrors(cropPeriodValidationErrors);
      setIsLoading(false);
      message({ content: t("errors.required"), icon: "error" });
      return;
    }

    const selectedCropData = practicesCrops?.find((crop) => crop.id === cropToValidate.cropId);
    const validateResidues = selectedCropData && selectedCropData.residue && !selectedCropData.residueHidden;
    if (validateResidues) {
      const hasResidueValue = form.getFieldValue(["crop", "hasResidue"]);
      if (hasResidueValue === null) {
        form.setFields([{ name: ["crop", "hasResidue"], errors: [t("modules:bauEntryDrawer.crops.residueLeft.error")] }]);
        setIsLoading(false);
        message({ content: t("errors.required"), icon: "error" });
        return;
      }
    }

    if (cropPeriodValidationErrors.length) {
      // Find first error field and scroll to it
      const firstError = cropPeriodValidationErrors[0];
      const errorElement = document.querySelector(`[data-field-id="${firstError.fieldId}"]`);
      errorElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      setCropPeriodValidationErrors(cropPeriodValidationErrors);

      setTimeout(() => setCropPeriodValidationErrors([]), 4000);
      setIsLoading(false);
      message({ content: firstError.message, icon: "error" });
    } else {
      try {
        // Handle warning (e.g. '0' yield)
        const fieldErrors = form.getFieldsError();
        const warnings = fieldErrors.filter((fieldError) => fieldError.warnings && fieldError.warnings.length > 0);
        const isZeroYieldWarning = warnings.filter((warning) => warning.name.includes("yieldPerHa")).length > 0;
        const errors = fieldErrors.filter((fieldError) => fieldError.errors && fieldError.errors.length > 0);

        if (errors.length > 0) return;
        if (!skipWarning && isZeroYieldWarning) {
          setShowExceptZeroValueWarning(true);
          return;
        }

        const brokenUndersowingCropNames = getUndersowingCropsInvalidatedByChange(cropToValidate, allCrops, carbonProgram).map((crop) => crop.cropName);
        if (!skipWarning && brokenUndersowingCropNames.length > 0) {
          setUndersowingCropsBrokenByChange(brokenUndersowingCropNames);
          setIsLoading(false);
          return;
        }

        const formValues = form.getFieldsValue(true);

        const { seedingDateRange, harvestDateRange, speciesCount, ...restFormValues } = formValues.crop;

        const diverseCoverCropIncentive = getDiverseCoverCropIncentive(carbonProgram);
        const isCropEligibleForSpeciesCount = !!restFormValues.cropId && !!diverseCoverCropIncentive?.elementIds?.includes(restFormValues.cropId);

        const transformedCrop = {
          ...restFormValues,
          seedingPracticeType: restFormValues.seedingPracticeType || null,
          seedingStartDate: seedingDateRange?.[0]?.toISOString() || null,
          seedingEndDate: seedingDateRange?.[1]?.toISOString() || null,
          harvestStartDate: harvestDateRange?.[0]?.toISOString() || null,
          harvestEndDate: harvestDateRange?.[1]?.toISOString() || null,
          seedingVerificationStatus: existingCrop?.seedingVerificationStatus || null, // inject previous seeding verification status
          speciesCount: !isCropEligibleForSpeciesCount
            ? null
            : speciesCount === undefined
            ? null
            : speciesCount
            ? diverseCoverCropIncentive?.speciesCountThreshold ?? null
            : 1,
          // override wasHarvested if harvestIsMandatory to fix issue of wasHarvested being set to false in the past which would lead to the yield not getting stored
          ...(selectedCropData?.harvestIsMandatory && { wasHarvested: true }),
        };

        const { crop, isTillageApplied, isMineralApplied, isMineralActivated, isOrganicApplied, hasFertilizerManagement, ...formPracticeObject } = {
          ...formValues,
          crop: transformedCrop,
        };

        const formPractices = Object.entries(formPracticeObject as FormPracticeObject).map(([_, practiceData]) => {
          const { dateRange, ...rest } = practiceData;

          const startDate = (dateRange as moment.Moment[])?.[0]?.toISOString();
          const endDate = (dateRange as moment.Moment[])?.[1]?.toISOString();
          return {
            ...rest,
            startDate,
            endDate,
            fallbackDates: {
              startDate: startDate ?? null,
              endDate: endDate ?? null,
            },
          } as PracticeFCPO;
        });

        const requestBody = createCropPracticeRequestBody(
          crop,
          formPractices,
          isTillageApplied,
          isMineralApplied,
          isMineralActivated,
          isOrganicApplied,
          hasFertilizerManagement,
          [...existingFertilizers, ...existingTillages],
          isSoilPlus3DataGapFeatureActive
        );

        const isAdding = !existingCrop?.id || isSideCropRecommendationPlaceholder;

        if (!isAdding) {
          await updateFieldCropPractice({
            requestBody,
            cropId: existingCrop.id,
          });
        } else {
          await addFieldCropPractice({
            requestBody: { ...requestBody, crop: { ...requestBody.crop, cropMixId: cmcMixCropFCPO.cropMixId } },
          });
        }

        // The backend completes a pending editing request when it detects the underlying data actually changed,
        // not because this call was made — so only submit a comment when the farmer actually left one.
        const pendingEditingRequestIds = [
          ...(pendingCropVerificationRequest ? [pendingCropVerificationRequest.id] : []),
          ...(pendingSeedingVerificationRequest ? [pendingSeedingVerificationRequest.id] : []),
          ...pendingTillageVerificationRequests.map((request) => request.id),
          ...pendingFertilizerVerificationRequests.map((request) => request.id),
        ];

        const editingRequestIdsWithComments = pendingEditingRequestIds.filter((requestId) => !!formValues.editingRequestComment?.[requestId]);

        if (editingRequestIdsWithComments.length) {
          await Promise.all(
            editingRequestIdsWithComments.map((requestId) =>
              submitEditingRequestComment({
                requestId,
                userComment: formValues.editingRequestComment![requestId] as string,
              })
            )
          );
        }

        if (existingCrop.cropMixId) {
          navigate(basePath);
        }

        message({ content: t("cropForm.success"), icon: "tick" });
        setShowDrawer(false);

        if (showDrawerFromCMC) {
          setShowDrawerFromCMC(false);
        }
      } catch {
        // form.validateFields() rejects when there are validation errors, which is expected
        // We handle this silently since we show validation errors through form.setFields above
      } finally {
        setIsLoading(false);
      }
    }
  };

  const incomplete = () => {
    message({ content: t("errors.required"), icon: "error" });
  };

  const scrollToRef = useCallback((ref: React.RefObject<HTMLDivElement | null>) => {
    ref?.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, []);

  const bundleHasConfirmedData = (bundleSlug: string) => availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.hasConfirmedData;

  const isHarvestMonitoringPeriod = carbonProgram?.fieldsManagement.monitoringPeriodType === "harvest";

  // same harvest-to-harvest baseline period as the field's row on the timeline
  const harvestBaselinePeriod = useMemo(() => {
    if (!isHarvestMonitoringPeriod || !fieldPractices) return null;

    const fieldCropItems = createCropItems(fieldPractices, field.id, field.baselineYears ?? [], fieldIsEnrolled, t);
    return getFieldBaselinePeriod(field, fieldCropItems, true, carbonProgram ?? null);
  }, [isHarvestMonitoringPeriod, fieldPractices, field, fieldIsEnrolled, t, carbonProgram]);

  const seedingDate = moment(existingCrop.seedingStartDate ?? existingCrop.fallbackDates.seedingStartDate);
  const isCropInBaselineYear =
    isHarvestMonitoringPeriod && existingCrop.cropType !== CROP_TYPE_FCPO.MAIN
      ? fieldIsEnrolled && !!harvestBaselinePeriod && isSownInBaselinePeriod(seedingDate, harvestBaselinePeriod)
      : fieldIsEnrolled && !!field.baselineYears?.includes(existingCrop.year ?? year);

  const requirementConfig = getInputRequirementConfig(carbonProgram ?? null, availablePracticeBundles, existingCrop, fieldIsEnrolled);

  // Check requirement config to see if there are even possible optional inputs
  const hideOptionalPracticeToggle = !Object.values(requirementConfig || {}).some((level) => level === "optional");

  const isNotUndersowing = existingCrop.cropType !== CROP_TYPE_FCPO.UNDERSOWING;
  const isNewCrop = !existingCrop.id;
  const isAdminOrImpersonator = getEnvironment().admin || isImpersonating();
  const canDeleteCrop = !isNewCrop && (!existingCrop.isConfirmed || isAdminOrImpersonator) && !isNewCropMix;

  const checkShowEnrolmentNudgeCard = (bundleSlug: "tillages" | "organicFertilizers") => {
    const bundle = practiceBundles.find((b) => b.slug === bundleSlug);
    const bundleIsActive = !!bundle?.active;
    const bundleIsEnrollable = !!bundle?.enrollable;

    return isNotUndersowing && fieldIsEnrolled && isCurrentYear && !bundleIsActive && bundleIsEnrollable;
  };

  const showTillageEnrolmentNudgeCard = checkShowEnrolmentNudgeCard(PRACTICE_SLUG.TILLAGES);
  const showFertilizerEnrolmentNudgeCard = checkShowEnrolmentNudgeCard(PRACTICE_SLUG.ORGANIC_FERTILIZERS);

  const cropYear = Number(existingCrop.year);

  // A pending editing request for a bundle means the farmer needs to be able to add/change data in it,
  // even if the bundle as a whole is otherwise locked (bundleHasConfirmedData) for the season.
  const hasPendingTillageRequest = pendingTillageVerificationRequests.length > 0;
  const hasPendingOrganicFertilizerRequest = pendingFertilizerVerificationRequests.some(
    (request) => request.requestType === EDITING_REQUEST_TYPE.ORGANIC_FERTILIZER
  );
  const hasPendingMineralFertilizerRequest = pendingFertilizerVerificationRequests.some(
    (request) => request.requestType === EDITING_REQUEST_TYPE.MINERAL_FERTILIZER
  );
  const hasPendingSeedingRequest = !!pendingSeedingVerificationRequest;

  const disableSeedingInputs =
    !hasPendingSeedingRequest &&
    !missingCropRequestYear &&
    getPracticeInputsDisabled({
      isPartOfSoilPlus3DataCollection: !!existingCrop.seedingSoilPlus3DataCollection,
      baselineYears: baselineYearsByPracticeType?.seedings,
      cropYear,
      activeFarmReportingYear,
      isDataSubmitted,
      lastSubmissionYear: submissionStatusValue?.lastSubmissionYear,
      bundleHasConfirmedData: bundleHasConfirmedData(PRACTICE_SLUG.SEEDINGS),
      isAdmin: isAdminOrImpersonator,
    });

  const disableTillageInputs = existingTillages.every((tillage) =>
    getIsSoilPlus3DataCollectionEditable(tillage.soilPlus3DataCollection, submissionStatusValue?.lastSubmissionYear)
  )
    ? false
    : !hasPendingTillageRequest &&
      getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.tillages, cropYear, firstEditableYear) &&
      !!bundleHasConfirmedData(PRACTICE_SLUG.TILLAGES) &&
      !isAdminOrImpersonator;

  const disableOrganicFertilizersInputs = existingFertilizers
    .filter((fertilizer) => fertilizer.type === "organicFertilizers")
    .every((organic) => getIsSoilPlus3DataCollectionEditable(organic.soilPlus3DataCollection, submissionStatusValue?.lastSubmissionYear))
    ? false
    : !hasPendingOrganicFertilizerRequest &&
      getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.organicFertilizers, cropYear, firstEditableYear) &&
      !!bundleHasConfirmedData(PRACTICE_SLUG.ORGANIC_FERTILIZERS) &&
      !isAdminOrImpersonator;

  const disableMineralFertilizersInputs =
    !hasPendingMineralFertilizerRequest &&
    !!existingFertilizers.filter((fertilizer) => fertilizer.type === "mineralFertilizers").length &&
    getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.mineralFertilizers, cropYear, firstEditableYear) &&
    !!bundleHasConfirmedData(PRACTICE_SLUG.MINERAL_FERTILIZERS) &&
    !isAdminOrImpersonator;

  // Currently there is only one type of fertilizer management (4R), but I have set this up to handle multiple in the future
  // If undersowing or cover crop, we never want to show the input therefore we don't want to set the existing value
  const fertilizerManagement =
    existingCrop.cropType === CROP_TYPE_FCPO.MAIN
      ? fertilizerManagements.find((management) => management.year === year && management.fieldCropId === existingCrop.id)
      : null;

  const onClose = () => {
    if (fieldsTouched) {
      setShowModal(true);
    } else {
      setShowDrawerFromCMC(false);
      setShowDrawer(false);
    }

    // Reset validation state when drawer closes
    setInitialValidationDone(false);
  };

  const onCropSelectionChanged = (crop: PracticeCrop) => {
    // I am using the selected crop's actual calendar dates as anchor points instead of the reporting `year` :D
    // for inventory programs that is the reporting year, which can be a calendar year ahead
    const selectedYear =
      getCropYearFromDates(
        existingCrop.cropType,
        moment(existingCrop.seedingStartDate ?? existingCrop.fallbackDates.seedingStartDate ?? undefined),
        moment(existingCrop.harvestEndDate ?? existingCrop.fallbackDates.harvestEndDate ?? undefined)
      ) ?? year;
    const recommendedDates = getCropRecommendedDates({ crop, selectedYear, cropType: existingCrop.cropType });
    if (recommendedDates) {
      setRecommendedSeedingDate(recommendedDates.recommendedSeedingDate.toISOString());
      setRecommendedHarvestDate(recommendedDates.recommendedHarvestDate.toISOString());
    }
  };

  const handleConfirmZeroModal = async (e: React.MouseEvent<HTMLButtonElement>) => {
    await onSubmit(e, true);
    setShowExceptZeroValueWarning(false);
    onClose();
  };

  // Reset validation state when drawer closes
  useEffect(() => {
    if (!showDrawer) {
      setInitialValidationDone(false);
    }
  }, [showDrawer]);

  useEffect(() => {
    if (!formWithOnLoadValidation || !showDrawer || initialValidationDone || !requirementConfig) return;

    let timeoutId: NodeJS.Timeout;
    let isCancelled = false;

    const attemptValidation = async (attempts: number = 0) => {
      if (isCancelled) return;

      if (attempts < MAX_VALIDATION_ATTEMPTS) {
        const refsAvailable = seedingRef.current && tillageRef.current && fertilizerRef.current;
        if (!refsAvailable) {
          timeoutId = setTimeout(() => attemptValidation(attempts + 1), VALIDATION_DELAY_MS);
          return;
        }
      }

      try {
        await form.validateFields();
      } catch {
        // validation failed; mount-only isRequired checks will run below
      }

      if (isCancelled) return;

      // mount-only: show errors for tillage/fertilizer yes-no, seeding type and yield when shown and empty.
      // Soil+3 asks for every shown input, RegionalValues only for required ones (enrolled bundles).
      const mountOnlyErrors: { name: string | string[]; errors: string[] }[] = [];
      const isSoilPlus3DataGapActive = isSoilPlus3DataGapActiveForProgram(carbonProgram);
      const shouldFlagIfEmpty = (level?: RequirementLevel) => isSoilPlus3DataGapActive || isRequired(level);

      const showTillage = shouldShowInput(requirementConfig?.tillages, optionalInputsAreShown) || existingTillages.length > 0;
      if (showTillage && shouldFlagIfEmpty(requirementConfig?.tillages)) {
        const val = form.getFieldValue("isTillageApplied");
        if (val === null || val === undefined) {
          mountOnlyErrors.push({ name: "isTillageApplied", errors: [t("tillageForm.errorMessages.tillageRequired")] });
        }
      }

      const showOrganic = shouldShowInput(requirementConfig?.organicFertilizers, optionalInputsAreShown) || organicFertilizers.length > 0;
      if (showOrganic && shouldFlagIfEmpty(requirementConfig?.organicFertilizers)) {
        const val = form.getFieldValue("isOrganicApplied");
        if (val === null || val === undefined) {
          mountOnlyErrors.push({ name: "isOrganicApplied", errors: [t("fertilizerForm.fertilizerRequired")] });
        }
      }

      const showMineral = shouldShowInput(requirementConfig?.mineralFertilizers, optionalInputsAreShown) || mineralFertilizers.length > 0;
      if (showMineral && shouldFlagIfEmpty(requirementConfig?.mineralFertilizers)) {
        const val = form.getFieldValue("isMineralApplied");
        if (val === null || val === undefined) {
          mountOnlyErrors.push({ name: "isMineralApplied", errors: [t("fertilizerForm.fertilizerRequired")] });
        }
      }

      const showSeeding = shouldShowInput(requirementConfig?.seedings, optionalInputsAreShown) || !!existingCrop.seedingPracticeType;
      if (showSeeding && shouldFlagIfEmpty(requirementConfig?.seedings)) {
        const val = form.getFieldValue(["crop", "seedingPracticeType"]);
        if (val === null || val === undefined || val === "") {
          mountOnlyErrors.push({ name: ["crop", "seedingPracticeType"], errors: [t("cropForm.errorMessages.seedingTypeRequired")] });
        }
      }

      // mount-only: biomass management (hasResidue) — always required when shown
      const hasResidue = form.getFieldValue(["crop", "hasResidue"]);
      if (hasResidue === null || hasResidue === undefined) {
        mountOnlyErrors.push({ name: ["crop", "hasResidue"], errors: [t("modules:bauEntryDrawer.crops.residueLeft.error")] });
      }

      // mount-only: show errors for missing tillage application dates and depth
      const nonZeroTillages = existingTillages.filter((tillage) => tillage.assignedPracticeId !== ZERO_UUID);
      nonZeroTillages.forEach((tillage, index) => {
        if (isRequired(requirementConfig?.dates) && !tillage.endDate) {
          mountOnlyErrors.push({ name: [`tillage-${index}`, "dateRange"], errors: [t("tillageForm.errorMessages.dateRangeRequired")] });
        }
        if (isRequired(requirementConfig?.tillageDepth) && typeof tillage.tillageDepth !== "number") {
          mountOnlyErrors.push({ name: [`tillage-${index}`, "tillageDepth"], errors: [t("tillageForm.errorMessages.tillageDepthRequired")] });
        }
      });

      // mount-only: show errors for partial seeding/harvest date ranges (form rules only check one side)
      // skip for historical crops (past years) since seedingEndDate/harvestStartDate were not collected historically
      if (isRequiredForSaving(requirementConfig?.dates) && year >= currentYear) {
        const seedingRange = form.getFieldValue(["crop", "seedingDateRange"]) as (moment.Moment | null)[] | null;
        if (!seedingRange?.[0] || !seedingRange?.[1]) {
          mountOnlyErrors.push({ name: ["crop", "seedingDateRange"], errors: [t("cropForm.errorMessages.seedingDateRequired")] });
        }
        const harvestRange = form.getFieldValue(["crop", "harvestDateRange"]) as (moment.Moment | null)[] | null;
        if (!harvestRange?.[0] || !harvestRange?.[1]) {
          mountOnlyErrors.push({ name: ["crop", "harvestDateRange"], errors: [t("cropForm.errorMessages.harvestDateRequired")] });
        }
      }

      // mount-only: show error for missing yield when shown and empty
      const yieldValue = form.getFieldValue(["crop", "yieldPerHa"]);
      const showYield = shouldShowInput(requirementConfig?.yield, optionalInputsAreShown) || existingCrop.yieldPerHa != null;
      if (showYield && shouldFlagIfEmpty(requirementConfig?.yield) && yieldValue == null) {
        mountOnlyErrors.push({ name: ["crop", "yieldPerHa"], errors: [t("cropForm.errorMessages.yieldRequired")] });
      }

      if (mountOnlyErrors.length > 0) {
        form.setFields(mountOnlyErrors);
      }

      if (!isCancelled) setInitialValidationDone(true);
    };

    timeoutId = setTimeout(() => attemptValidation(), VALIDATION_DELAY_MS);
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showDrawer,
    requirementConfig,
    initialValidationDone,
    formWithOnLoadValidation,
    form,
    scrollToRef,
    optionalInputsAreShown,
    existingTillages.length,
    existingCrop.seedingPracticeType,
    carbonProgram,
    t,
  ]);

  return (
    <>
      <Drawer
        dataTestId="add-edit-crop-drawer"
        title={t("fieldHeadlineCard.headline")}
        visible={showDrawer}
        onClose={onClose}
        destroyOnHidden={true}
        className="add-edit-crop-drawer"
        zIndex={showDrawerFromCMC ? 1003 : undefined} // higher z-index to show it on top of the CMC drawer
      >
        <FieldHeadline
          headline=""
          field={field}
          shape={fieldShape}
          isLoading={fieldShapeLoading || isLoadingProgramOutcomes}
          isEnrolled={fieldIsEnrolled}
          isBaseline={isCropInBaselineYear}
          onDelete={canDeleteCrop ? () => setShowCropDeletionModal(true) : undefined}
        />
        {/* TODO (Alp): Remove this after Soil+ 3.0 is done */}
        <HideFeature hide={isSoilPlus3DataGapFeatureActive}>
          <>
            {existingCrop.cropType !== CROP_TYPE_FCPO.UNDERSOWING && (
              <HideFCPOFeatureForUnenrolledFarms>
                <PracticeBundleControls
                  bundles={fieldIsEnrolled ? practiceBundles.filter((practiceBundle) => practiceBundle.slug !== PRACTICE_SLUG.CROPS) : []}
                  optionalInputsAreShown={optionalInputsAreShown || !farmIsEnrolledInCarbonProgram}
                  setOptionalInputsAreShown={setOptionalInputsAreShown}
                  onBundleActiveStateChange={handleBundleActiveStateChange}
                  isCurrentYear={isCurrentYear}
                  onPastYearToggle={handlePastYearToggle}
                  hideOptionalPractices={hideOptionalPracticeToggle}
                  hidePracticeIcons={isInventoryProgram}
                  viewType={viewType}
                />
              </HideFCPOFeatureForUnenrolledFarms>
            )}{" "}
          </>
        </HideFeature>

        {isLoadingProgramOutcomes ? (
          <FormSkeleton cropType={existingCrop.cropType} />
        ) : (
          <Form
            form={form}
            name="cropForm"
            onFinishFailed={incomplete}
            onValuesChange={() => setFieldsTouched(true)}
            preserve={true} // from my research, this helps to prevent re-validation from clearing touched state
          >
            {isSubmissionFeatureEnabled && !getEnvironment().admin && pendingCropVerificationRequest && (
              <EditingRequestBanner
                key={EDITING_REQUEST_TYPE.CROP}
                type={EDITING_REQUEST_TYPE.CROP}
                farmId={farmId!}
                id={existingCrop.id}
                requestId={pendingCropVerificationRequest.id}
                onSubmitComment={isNewCrop ? submitMissingCropRequestComment : undefined}
              />
            )}

            <AddEditCropForm
              setShowDrawer={setShowDrawer}
              seedingExtraContent={
                isSubmissionFeatureEnabled &&
                !getEnvironment().admin &&
                pendingSeedingVerificationRequest && (
                  <EditingRequestBanner
                    key={EDITING_REQUEST_TYPE.SEEDING}
                    type={EDITING_REQUEST_TYPE.SEEDING}
                    farmId={farmId!}
                    id={existingCrop.seedingPracticeId!}
                    requestId={pendingSeedingVerificationRequest.id}
                  />
                )
              }
              baselineYears={baselineYearsByPracticeType}
              crop={existingCrop}
              cropPeriodValidationErrors={cropPeriodValidationErrors}
              dataToReview={dataToReview}
              disableTillageAndSeedingInputs={disableSeedingInputs}
              field={field}
              ref={seedingRef}
              recommendedSeedingDate={recommendedSeedingDate}
              recommendedHarvestDate={recommendedHarvestDate}
              onCropSelectionChanged={onCropSelectionChanged}
              year={year}
              optionalInputsAreShown={optionalInputsAreShown}
              requirementConfig={requirementConfig}
            />

            {/* Tillage Form */}
            {(shouldShowInput(requirementConfig?.tillages, optionalInputsAreShown) || existingTillages.length > 0) && (
              <>
                <AddEditTillageForm
                  cropId={existingCrop.id}
                  existingTillages={existingTillages}
                  ref={tillageRef}
                  disabled={disableTillageInputs}
                  baselineYears={baselineYearsByPracticeType.tillages}
                  recommendedSeedingDate={recommendedSeedingDate}
                  year={year}
                  requirementConfig={requirementConfig}
                  optionalInputsAreShown={optionalInputsAreShown}
                  requirePreciseTillageDepth={carbonProgram?.fieldsManagement.requirePreciseTillageDepth === true}
                  requireTillageSoilMixing={carbonProgram?.fieldsManagement.requireTillageSoilMixing === true}
                  editingRequests={pendingTillageVerificationRequests ?? []}
                  isSubmissionFeatureEnabled={isSubmissionFeatureEnabled}
                  farmId={farmId!}
                  renderEntryTag={renderReviewTag}
                />
              </>
            )}

            {/* TODO (Alp): Remove this after Soil+ 3.0 is done */}
            <HideFeature hide={isSoilPlus3DataGapFeatureActive}>
              <>
                {showTillageEnrolmentNudgeCard && !isInventoryProgram && (
                  <PracticeEnrolmentNudgeCard
                    practiceBundleSlug={PRACTICE_SLUG.TILLAGES}
                    onClick={() =>
                      handleBundleActiveStateChange(PRACTICE_SLUG.TILLAGES, !practiceBundles.find((bundle) => bundle.slug === PRACTICE_SLUG.TILLAGES)?.active)
                    }
                    disabled={!!existingCrop.isConfirmed}
                  />
                )}
              </>
            </HideFeature>

            {/* Fertilizer Forms */}
            {(shouldShowInput(requirementConfig?.fertilizerManagement, optionalInputsAreShown) || fertilizerManagement) && (
              <FertilizerManagementYesNoForm
                initialValue={fertilizerManagement?.implements4R ?? null}
                requirementLevel={requirementConfig?.fertilizerManagement}
                disabled={!!existingCrop.isConfirmed && !isAdminOrImpersonator}
              />
            )}

            {(shouldShowInput(requirementConfig?.organicFertilizers, optionalInputsAreShown) || organicFertilizers.length > 0) && (
              <AddEditOrganicAndMineralFertilizers
                chosenFertilizerType="organic"
                disabled={disableOrganicFertilizersInputs}
                existingFertilizers={organicFertilizers}
                ref={fertilizerRef}
                baselineYears={baselineYearsByPracticeType.organicFertilizers}
                recommendedSeedingDate={recommendedSeedingDate}
                year={year}
                currency={carbonProgram?.currency}
                requirementLevel={requirementConfig?.organicFertilizers}
                practiceBundle={practiceBundles.find((practice) => practice.slug === PRACTICE_SLUG.ORGANIC_FERTILIZERS)}
                onBundleActivation={handleBundleActiveStateChange}
                requirementConfig={requirementConfig}
                carbonProgramMethodology={carbonProgram?.accountingMethodology}
                editingRequests={pendingFertilizerVerificationRequests}
                isSubmissionFeatureEnabled={isSubmissionFeatureEnabled}
                farmId={farmId}
                renderEntryTag={renderReviewTag}
              />
            )}

            {(shouldShowInput(requirementConfig?.mineralFertilizers, optionalInputsAreShown) || mineralFertilizers.length > 0) && (
              <AddEditOrganicAndMineralFertilizers
                chosenFertilizerType="mineral"
                disabled={disableMineralFertilizersInputs}
                existingFertilizers={mineralFertilizers}
                ref={fertilizerRef}
                baselineYears={baselineYearsByPracticeType.mineralFertilizers}
                recommendedSeedingDate={recommendedSeedingDate}
                year={year}
                currency={carbonProgram?.currency}
                requirementLevel={requirementConfig?.mineralFertilizers}
                practiceBundle={practiceBundles.find((practice) => practice.slug === PRACTICE_SLUG.MINERAL_FERTILIZERS)}
                onBundleActivation={handleBundleActiveStateChange}
                requirementConfig={requirementConfig}
                carbonProgramMethodology={carbonProgram?.accountingMethodology}
                fieldIsEnrolled={fieldIsEnrolled}
                editingRequests={pendingFertilizerVerificationRequests}
                isSubmissionFeatureEnabled={isSubmissionFeatureEnabled}
                farmId={farmId}
                renderEntryTag={renderReviewTag}
              />
            )}

            {/* TODO (Alp): Remove this after Soil+ 3.0 is done */}
            <HideFeature hide={isSoilPlus3DataGapFeatureActive}>
              <>
                {showFertilizerEnrolmentNudgeCard && !isInventoryProgram && (
                  <PracticeEnrolmentNudgeCard
                    practiceBundleSlug={PRACTICE_SLUG.ORGANIC_FERTILIZERS}
                    onClick={() =>
                      handleBundleActiveStateChange(
                        PRACTICE_SLUG.ORGANIC_FERTILIZERS,
                        !practiceBundles.find((bundle) => bundle.slug === PRACTICE_SLUG.ORGANIC_FERTILIZERS)?.active
                      )
                    }
                    disabled={!!existingCrop.isConfirmed}
                  />
                )}
              </>
            </HideFeature>

            <StickyFooterButtons
              absolute
              extras={
                isNewCrop && (
                  <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                    {t("allFieldView:bulkCropDrawerFooterHint")}
                  </Typography>
                )
              }
            >
              <div className="row-space-between">
                <Button dataTestId="save-crop-drawer" loading={isLoading} type="primary" htmlType="submit" onClick={onSubmit}>
                  {t("saveCropDrawer")}
                </Button>
              </div>
            </StickyFooterButtons>
          </Form>
        )}
      </Drawer>
      <Modal
        showModal={showModal}
        onOk={(e) => {
          onSubmit(e);
          setShowModal(false);
        }}
        onCancel={() => {
          setShowModal(false);
          setShowDrawer(false);
        }}
        okText={t("saveCropDrawerSave")}
        cancelText={t("saveCropDrawerDiscard")}
        zIndex={10002}
        destroyOnHidden={true}
        content={
          <>
            <Typography variant="h3">{t("saveCropDrawerConfirm")}</Typography>
          </>
        }
      />
      <Modal
        dataTestId="crop-form-delete"
        showModal={showCropDeletionModal}
        title={t("cropForm.cropDeletionModal.title")}
        description={t("cropForm.cropDeletionModal.description")}
        okText={t("cropForm.cropDeletionModal.confirm")}
        cancelText={t("cropForm.cropDeletionModal.cancel")}
        onCancel={() => setShowCropDeletionModal(false)}
        onOk={async () => {
          setShowCropDeletionModal(false);
          await handleCropDeletion();
        }}
      />
      <Modal
        showModal={showExceptZeroValueWarning}
        onOk={handleConfirmZeroModal}
        onCancel={() => {
          setShowExceptZeroValueWarning(false);
          setIsLoading(false);
        }}
        title={t("tasksDrawer.tasks.yield.drawer.confirmZeroValueModal.title")}
        okText={t("tasksDrawer.tasks.yield.drawer.confirmZeroValueModal.confirm")}
        cancelText={t("tasksDrawer.tasks.yield.drawer.confirmZeroValueModal.cancel")}
        destroyOnHidden
        style={{ width: "40rem" }}
        content={
          <Flex vertical gap="1rem">
            <Typography variant="paragraph-m" className="margin-0">
              {t("tasksDrawer.tasks.yield.drawer.confirmZeroValueModal.subtitle")}
            </Typography>
            <AlertBanner
              className="margin-top-0"
              description={t("allFieldView:tasksDrawer.tasks.yield.drawer.confirmZeroValueModal.banner")}
              type="warning"
              iconName="tabler/info-circle"
              closeable={false}
              iconAlign="center"
              gap={0}
            />
            <Divider />
            <Typography variant="paragraph-m" className="margin-0">
              {t("tasksDrawer.tasks.yield.drawer.confirmZeroValueModal.question")}
            </Typography>
          </Flex>
        }
      />
      <UndersowingOverlapWarningModal
        dataTestId="crop-form-undersowing-overlap-warning"
        cropNames={undersowingCropsBrokenByChange}
        onCancel={() => setUndersowingCropsBrokenByChange([])}
        onConfirm={(e) => {
          setUndersowingCropsBrokenByChange([]);
          onSubmit(e, true);
        }}
      />
    </>
  );
};

const FormSkeleton = ({ cropType }: { cropType: CropFCPO["cropType"] }) => {
  if (cropType === CROP_TYPE_FCPO.UNDERSOWING) {
    return <Skeleton type="block" height="15rem" className="margin-top-200" />;
  }

  return (
    <>
      <Skeleton type="block" height="15rem" className="margin-top-200" />
      <Skeleton type="block" height="5rem" />
      <Skeleton type="block" height="5rem" />
    </>
  );
};
